import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const Lightbox = ({ open, images, index, onClose, setIndex }) => {
  if (!open || !images?.length) return null;
  const handleKey = (e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "ArrowLeft" && index > 0) setIndex(index - 1);
    if (e.key === "ArrowRight" && index < images.length - 1) setIndex(index + 1);
  };
  return (
    <Box
      onKeyDown={handleKey}
      tabIndex={-1}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1500,
        backgroundColor: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: "#fff",
          backgroundColor: "rgba(255,255,255,0.1)",
          "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
        }}
      >
        <CloseIcon />
      </IconButton>
      {index > 0 && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setIndex(index - 1);
          }}
          sx={{
            position: "absolute",
            left: 16,
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      {index < images.length - 1 && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setIndex(index + 1);
          }}
          sx={{
            position: "absolute",
            right: 16,
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.1)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
      <Box
        component="img"
        src={images[index]}
        alt=""
        onClick={(e) => e.stopPropagation()}
        sx={{
          maxWidth: "90vw",
          maxHeight: "90vh",
          objectFit: "contain",
          borderRadius: "8px",
        }}
      />
    </Box>
  );
};

const ImageGallery = ({
  title,
  description,
  tag,
  images,
  uploading,
  onUpload,
  onDelete,
  readOnly = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (!images?.length) {
      setLightboxOpen(false);
    }
  }, [images?.length]);

  const triggerFileSelect = () => {
    if (readOnly || uploading) return;
    inputRef.current?.click();
  };

  const handleFiles = (filesList) => {
    if (!filesList?.length) return;
    if (filesList.length > 25) {
      alert("You can upload a maximum of 25 images at a time.");
      return;
    }
    onUpload?.(Array.from(filesList), tag);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (readOnly) return;
    const filesList = e.dataTransfer?.files;
    handleFiles(filesList);
  };

  const handleChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        padding: "24px",
        backgroundColor: "#fff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 500,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: "13px", color: "#6B7280", mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Box>

      {!readOnly && (
        <Box
          onClick={triggerFileSelect}
          onDragOver={(e) => {
            e.preventDefault();
            if (!readOnly) setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
          sx={{
            cursor: uploading ? "default" : "pointer",
            border: "1px dashed",
            borderColor: dragActive ? "#374151" : "#D1D5DB",
            backgroundColor: dragActive ? "#F9FAFB" : "transparent",
            borderRadius: "12px",
            padding: "40px 16px",
            textAlign: "center",
            transition: "all 0.15s ease-in-out",
            "&:hover": {
              borderColor: uploading ? "#D1D5DB" : "#9CA3AF",
            },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={handleChange}
          />
          {uploading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={18} />
              <Typography sx={{ color: "#374151", fontSize: "14px" }}>
                Uploading...
              </Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ color: "#374151", fontSize: "15px", fontWeight: 500 }}>
                Drag and drop images here or{" "}
                <Box component="span" sx={{ color: "#0D0D0E", fontWeight: 600 }}>
                  upload images
                </Box>
              </Typography>
              <Typography sx={{ color: "#9CA3AF", fontSize: "12px", mt: 0.5 }}>
                You can upload up to 25 images at a time
              </Typography>
            </>
          )}
        </Box>
      )}

      {images?.length ? (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            mt: 3,
            gridTemplateColumns: {
              xs: "repeat(1, 1fr)",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
              xl: "repeat(5, 1fr)",
            },
          }}
        >
          {images.map((img, index) => (
            <Box
              key={img.id}
              sx={{
                position: "relative",
                borderRadius: "12px",
                overflow: "hidden",
                border: "1px solid #F3F4F6",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                "&:hover .design-img": { transform: "scale(1.04)" },
              }}
            >
              <Box
                className="design-img"
                component="img"
                src={img.path || img.image || img.url}
                alt=""
                onClick={() => {
                  setLightboxIndex(index);
                  setLightboxOpen(true);
                }}
                sx={{
                  width: "100%",
                  height: 208,
                  objectFit: "cover",
                  cursor: "pointer",
                  transition: "transform 0.3s ease-in-out",
                  display: "block",
                }}
              />
              {!readOnly && onDelete && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(img.id, tag);
                  }}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: isMobile
                      ? "#fff"
                      : "rgba(255,255,255,0.85)",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    width: 32,
                    height: 32,
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                >
                  <DeleteOutlineIcon
                    sx={{ fontSize: 18, color: "#DC2626" }}
                  />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      ) : (
        <Typography
          sx={{
            mt: 3,
            color: "#9CA3AF",
            fontSize: "14px",
            textAlign: "center",
            py: 4,
          }}
        >
          No images uploaded yet.
        </Typography>
      )}

      <Lightbox
        open={lightboxOpen}
        images={(images || []).map((i) => i.path || i.image || i.url)}
        index={lightboxIndex}
        setIndex={setLightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </Box>
  );
};

export default ImageGallery;
